import React, { useEffect, useState } from "react";
import { Input, Checkbox, message, Modal, Form, Button } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import "./login.css";
import drEasyApi, { setApiSlug } from "../../../Api/Customapi";
import { HiOutlineMail, HiOutlineUser } from "react-icons/hi";
import { GiPadlock, GiPhone } from "react-icons/gi";
import ForgotPasswordModal from "../../Modals/ForgotPasswordModal";
import VerificationModal from "../../Modals/VerificationModal";
import { GoogleLogin, useGoogleLogin } from "@react-oauth/google";
import { useDoctorQuery } from "../../../hooks/useDoctorQuery";
import LockedDoctor from "../../HomeSections/LockedDoctor";
import { useLanguage } from "../../../context/LanguageContext";
import LanguageSwitcher from "../../Ui/LanguageSwitcher";

export default function AuthForm({ active }) {
    const { t, isRTL } = useLanguage();
    const [loginForm] = Form.useForm();
    const [signupForm] = Form.useForm();

    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [shake, setShake] = useState(false);
    const [activeTab, setActiveTab] = useState(active ?? "login");

    // Verification modal states
    const [showVerificationModal, setShowVerificationModal] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [verificationEmail, setVerificationEmail] = useState("");
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(60);
    const [showForgotModal, setShowForgotModal] = useState(false);
    const [forgotEmail, setForgotEmail] = useState("");
    const [forgotStep, setForgotStep] = useState(1);
    const [resetCode, setResetCode] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [resetLoading, setResetLoading] = useState(false);
    const { data: doctor } = useDoctorQuery();
    console.log(doctor);

    // Store signup token for auto-login after verification
    const [signupToken, setSignupToken] = useState("");

    const { slug } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        localStorage.removeItem('docTkn');
    }, []);

    useEffect(() => {
        setApiSlug(slug);
    }, [slug]);

    // Cooldown timer for resend button
    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    // Clear forms when switching tabs
    useEffect(() => {
        loginForm.resetFields();
        signupForm.resetFields();
    }, [activeTab, loginForm, signupForm]);

    const handleLogin = async (values) => {
        setLoading(true);
        try {
            console.log("Starting login process");

            const endpoint = `/api/${slug}/User/Login`;
            const payload = { email: values.email, password: values.password };

            console.log("API endpoint:", endpoint);
            const response = await drEasyApi.post(endpoint, payload);
            console.log("API response:", response);

            const { token, role } = response.data;

            if (role !== 1) {
                message.error("Unauthorized access.");
                return;
            }

            // Store token based on remember me option
            const storage = rememberMe ? localStorage : sessionStorage;
            storage.setItem(`userTkn-${slug}`, token);

            message.success("Login successful!");
            navigate(`/${slug}/home`);

        } catch (error) {
            triggerErrorAnimation();

            if (error.response?.status === 401 || error.response?.status === 400) {
                const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0] || "";

                // Check if error is related to verification
                if (errorMessage.toLowerCase().includes("verify")) {
                    message.error("Please verify your email before logging in.");
                    setVerificationEmail(values.email);
                    setShowVerificationModal(true);
                    setResendCooldown(60)
                } else if (errorMessage.toLowerCase().includes("login invalid")) {
                    message.error("Invalid email or password");
                } else {
                    console.log(error);
                    message.error(errorMessage);
                }
            } else if (error.response?.data?.errors) {
                message.error(error.response.data.errors[0]);
            } else {
                message.error("Login failed. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleSignup = async (values) => {
        setLoading(true);
        try {
            console.log("Starting registration process");

            const endpoint = `/api/${slug}/User/RegisterAsUser`;
            const payload = {
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                phoneNumber: values.phoneNumber,
                password: values.password
            };

            console.log("API endpoint:", endpoint);
            const response = await drEasyApi.post(endpoint, payload);
            console.log("API response:", response);

            const { token } = response.data;
            setSignupToken(token);

            message.success("Registration successful! Please check your email for verification code.");
            setVerificationEmail(values.email);
            setShowVerificationModal(true);
            setResendCooldown(60)

            signupForm.resetFields();

        } catch (error) {
            triggerErrorAnimation();

            if (error.response?.status === 401 || error.response?.status === 400) {
                const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0] || "";
                message.error(errorMessage);
            } else if (error.response?.data?.errors) {
                message.error(error.response.data.errors[0]);
            } else {
                message.error("Registration failed. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        if (!verificationCode.trim()) {
            message.warning("Please enter the verification code.");
            return;
        }

        setVerificationLoading(true);
        try {
            const res = await drEasyApi.post(`/api/${slug}/User/Verify`, {
                email: verificationEmail,
                otpCode: verificationCode
            });
            const { token, role } = res.data;

            message.success("Email verified successfully! Logging you in...");

            // Auto-login after successful verification
            if (token) {
                // Store the token
                const storage = rememberMe ? localStorage : sessionStorage;
                storage.setItem(`userTkn-${slug}`, token);

                navigate(`/${slug}/home`);
            } else {
                setShowVerificationModal(false);
                setActiveTab("login");
            }

            setVerificationCode("");
            setSignupToken("");
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0] || "Verification failed. Please try again.";
            message.error(errorMessage);
        } finally {
            setVerificationLoading(false);
        }
    };

    const handleResendCode = async () => {
        setVerificationCode("")
        setResendLoading(true);
        try {
            await drEasyApi.post(`/api/${slug}/User/ResendOtp`, {
                email: verificationEmail
            });

            message.success("Verification code sent to your email!");
            setResendCooldown(60);
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0] || "Failed to resend code. Please try again.";
            message.error(errorMessage);
        } finally {
            setResendLoading(false);
        }
    };

    const triggerErrorAnimation = () => {
        setShake(true);
        setTimeout(() => setShake(false), 400);
    };

    const handleGoogleSignIn = async (response) => {
        try {
            console.log("Google Sign-In response:", response);

            // Send the Google ID token to your backend
            const apiResponse = await drEasyApi.post(`/api/${slug}/User/GoogleAuth`, {
                token: response.credential // This is the ID token (JWT)
            });

            console.log("Google Auth API response:", apiResponse);

            const { token, role } = apiResponse.data;

            // Validate if the role is 1 (user)
            if (role !== 1) {
                message.error("Unauthorized access.");
                return;
            }

            // Store token based on remember me option
            const storage = rememberMe ? localStorage : sessionStorage;
            storage.setItem(`userTkn-${slug}`, token);

            message.success("Google Sign-In successful!");
            navigate(`/${slug}/home`);

        } catch (error) {
            console.error("Google Sign-In error:", error);

            if (error.response?.status === 401 || error.response?.status === 400) {
                const errorMessage = error.response?.data?.message || error.response?.data?.errors?.[0] || "";
                message.error(errorMessage || "Google Sign-In failed. Please try again.");
            } else if (error.response?.data?.errors) {
                message.error(error.response.data.errors[0]);
            } else {
                message.error("Google Sign-In failed. Please try again.");
            }
        }
    };
    const handleResendResetCode = async () => {

        try {
            await drEasyApi.post(`/api/${slug}/User/ForgotPassword`, {
                email: forgotEmail
            });
            message.success("Reset code sent to your email.");
        } catch (err) {
            if (err.response?.data.errors[0].includes("not found")) {
                message.error("Your email not found, you need to Signup");
            } else {
                message.error(err.response?.data.errors[0] || "Failed to send reset email.");
            }
            throw err; // Re-throw to handle in modal
        }
    };

    const renderLoginForm = () => (
        <Form
            form={loginForm}
            layout="vertical"
            onFinish={handleLogin}
            className="space-y-4"
            size="large"
        >
            <Form.Item
                name="email"
                label={<span className="text-textPrimary text-lg">{t("auth.email")}</span>}
                rules={[
                    {
                        required: true,
                        message: t("validation.required")
                    },
                    {
                        type: "email",
                        message: t("validation.email")
                    }
                ]}
            >
                <Input
                    placeholder={t("auth.email")}
                    prefix={<HiOutlineMail className="text-placeHolder" />}
                    className="text-placeHolder placeholder:text-placeHolder"
                />
            </Form.Item>

            <Form.Item
                name="password"
                label={<span className="text-textPrimary text-lg">{t("auth.password")}</span>}
                rules={[
                    {
                        required: true,
                        message: t("validation.required")
                    }
                ]}
            >
                <Input.Password
                    placeholder={t("auth.password")}
                    prefix={<GiPadlock className="text-placeHolder" />}
                    className="text-placeHolder placeholder:text-placeHolder"
                />
            </Form.Item>

            <div className="flex items-center justify-between">
                <Checkbox
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="text-primary text-lg"
                >
                    {t("auth.rememberMe")}
                </Checkbox>
                <span
                    className="text-secondary text-lg cursor-pointer hover:underline"
                    onClick={() => {
                        setForgotStep(1);
                        setForgotEmail("");
                        setResetCode("");
                        setNewPassword("");
                        setShowForgotModal(true);
                        setResendCooldown(60)
                    }}
                >
                    {t("auth.forgotPassword")}
                </span>
            </div>

            <Button
                type=""
                htmlType="submit"
                loading={loading}
                className="w-full py-3 h-auto text-lg text-white font-semibold bg-primary hover:bg-primary_hover border-none"
            >
                {loading ? t("common.loading") : t("auth.login")}
            </Button>
        </Form>
    );

    const renderSignupForm = () => (
        <div>
            {!doctor?.features || doctor?.features.length == 0 || !doctor?.features.includes("patient_registration") ? <LockedDoctor /> :
                <Form
                    form={signupForm}
                    layout="vertical"
                    onFinish={handleSignup}
                    className="space-y-4"
                    size="large"
                >
                    <div className="flex gap-4">
                        <Form.Item
                            name="firstName"
                            label={<span className="text-textPrimary text-lg">{t("auth.firstName")}</span>}
                            className="flex-1 mb-0"
                            rules={[
                                {
                                    required: true,
                                    message: t("validation.required")
                                },
                                {
                                    min: 2,
                                    message: t("validation.minLength", { count: 2 })
                                }
                            ]}
                        >
                            <Input
                                placeholder={t("auth.firstName")}
                                prefix={<HiOutlineUser className="text-placeHolder" />}
                            />
                        </Form.Item>

                        <Form.Item
                            name="lastName"
                            label={<span className="text-textPrimary text-lg">{t("auth.lastName")}</span>}
                            className="flex-1 mb-0"
                            rules={[
                                {
                                    required: true,
                                    message: t("validation.required")
                                },
                                {
                                    min: 2,
                                    message: t("validation.minLength", { count: 2 })
                                }
                            ]}
                        >
                            <Input
                                placeholder={t("auth.lastName")}
                                prefix={<HiOutlineUser className="text-placeHolder" />}
                            />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="email"
                        label={<span className="text-textPrimary text-lg">{t("auth.email")}</span>}
                        rules={[
                            {
                                required: true,
                                message: t("validation.required")
                            },
                            {
                                type: "email",
                                message: t("validation.email")
                            }
                        ]}
                    >
                        <Input
                            placeholder={t("auth.email")}
                            prefix={<HiOutlineMail className="text-placeHolder" />}
                        />
                    </Form.Item>

                    <Form.Item
                        name="phoneNumber"
                        label={<span className="text-textPrimary text-lg">{t("auth.phone")}</span>}
                        rules={[
                            {
                                required: true,
                                message: t("validation.required")
                            },
                            {
                                pattern: /^[\+]?[0-9][\d]{0,15}$/,
                                message: t("validation.phone")
                            }
                        ]}
                    >
                        <Input
                            placeholder={t("auth.phone")}
                            prefix={<GiPhone className="text-placeHolder" />}
                        />
                    </Form.Item>

                    <Form.Item
                        name="password"
                        label={<span className="text-textPrimary text-lg">{t("auth.password")}</span>}
                        rules={[
                            {
                                required: true,
                                message: t("validation.required")
                            },
                            {
                                min: 6,
                                message: t("validation.password")
                            }
                        ]}
                    >
                        <Input.Password
                            placeholder={t("auth.password")}
                            prefix={<GiPadlock className="text-placeHolder" />}
                        />
                    </Form.Item>

                    <Form.Item
                        name="confirmPassword"
                        label={<span className="text-textPrimary text-lg">{t("auth.confirmPassword")}</span>}
                        dependencies={['password']}
                        rules={[
                            {
                                required: true,
                                message: t("validation.required")
                            },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error(t("validation.passwordMatch")));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            placeholder={t("auth.confirmPassword")}
                            prefix={<GiPadlock className="text-placeHolder" />}
                        />
                    </Form.Item>

                    <Button
                        type=""
                        htmlType="submit"
                        loading={loading}
                        className="w-full py-3 h-auto text-white text-lg font-semibold bg-primary hover:bg-primary_hover border-none"
                    >
                        {loading ? t("common.loading") : t("auth.signUp")}
                    </Button>
                </Form>}
        </div>

    );

    return (
        <div className="min-h-screen flex flex-col sm:flex-row sm:p-0 p-5 gap-y-3">
            {/* Left Side - Image + Welcome */}
            <div className="w-full sm:w-1/2 bg-primary sm:h-full sm:rounded-none rounded-full aspect-square text-white sm:p-10 flex flex-col justify-center items-center sm:py-10 sm:max-h-screen">
                <h1 className="text-3xl font-bold text-secondary sm:inline-block hidden">
                    {activeTab === "login" ? t("auth.welcomeBack") : t("auth.createAccount")}
                </h1>
                <p className="text-lg text-page text-center sm:inline-block hidden">
                    {activeTab === "login"
                        ? t("auth.loginSubtitle")
                        : t("auth.signupSubtitle")}
                </p>
                <img
                    src={require("../../../assets/login-illustration.png")}
                    alt="Medical Illustration"
                    className="object-contain sm:h-auto h-full"
                />
            </div>

            {/* Right Side - Auth Form */}
            <div className="w-full sm:w-1/2 2xl:max-w-3xl 2xl:mx-auto sm:p-10 flex flex-col items-center justify-center min-h-[50vh] sm:min-h-screen">
                <div className="flex justify-between items-center mb-10 w-full">
                    <div className="flex border border-primary rounded-xl overflow-hidden shadow-lg w-full">
                        <button
                            onClick={() => setActiveTab("login")}
                            className={`w-1/2 py-2 font-semibold text-lg transition duration-300 ${activeTab === "login" ? "bg-primary text-white" : "text-primary bg-transparent"
                                }`}
                        >
                            {t("auth.login")}
                        </button>
                        <button
                            onClick={() => setActiveTab("signup")}
                            className={`w-1/2 py-2 font-semibold text-lg transition duration-300 ${activeTab === "signup" ? "bg-primary text-white" : "text-primary bg-transparent"
                                }`}
                        >
                            {t("auth.signUp")}
                        </button>
                    </div>
                    <div className="ml-4">
                        <LanguageSwitcher />
                    </div>
                </div>

                <div className={`w-full ${shake ? "shake" : ""}`}>
                    {activeTab === "login" ? renderLoginForm() : renderSignupForm()}

                    <div className="flex items-center justify-center my-4">
                        <div className="flex-grow border-t border-gray-300"></div>
                        <span className="px-4 text-gray-500 text-sm font-medium">{t("auth.or")}</span>
                        <div className="flex-grow border-t border-gray-300"></div>
                    </div>
                    <div className="flex justify-center items-center w-full">

                        <GoogleLogin
                            onSuccess={handleGoogleSignIn}
                            onError={(error) => {
                                console.log('Google Sign-In Failed:', error);
                                message.error("Google Sign-In failed. Please try again.");
                            }}

                            size="large"

                            width={1000} 
                            text="continue_with"
                            
                            shape="square"
                            type="standard"
                            auto_select={false}

                        />
                    </div>
                </div>
            </div>

            <VerificationModal
                open={showVerificationModal}
                onClose={() => {
                    setShowVerificationModal(false);
                    setSignupToken(""); // Clear token if modal is closed
                }}
                email={verificationEmail}
                code={verificationCode}
                setCode={setVerificationCode}
                loading={verificationLoading}
                onVerify={handleVerifyCode}
                onResend={handleResendCode}
                resendLoading={resendLoading}
                resendCooldown={resendCooldown}
            />
            <ForgotPasswordModal
                open={showForgotModal}
                onClose={() => setShowForgotModal(false)}
                step={forgotStep}
                email={forgotEmail}
                setEmail={setForgotEmail}
                code={resetCode}
                setCode={setResetCode}
                newPassword={newPassword}
                setNewPassword={setNewPassword}
                loading={resetLoading}
                onSendResetCode={async () => {
                    if (!forgotEmail.trim()) return message.warning("Please enter your email.");
                    setResetLoading(true);
                    try {
                        await drEasyApi.post(`/api/${slug}/User/ForgotPassword`, { email: forgotEmail });
                        message.success("Reset code sent to your email.");
                        setForgotStep(2);
                    } catch (err) {
                        if (err.response?.data.errors[0].includes("not found")) {
                            message.error("Your email not found, you need to Signup");
                        } else {
                            message.error(err.response?.data.errors[0] || "Failed to send reset email.");
                        }
                    } finally {
                        setResetLoading(false);
                    }
                }}
                onResendCode={handleResendResetCode} // Add this line
                onResetPassword={async () => {
                    if (!resetCode.trim() || !newPassword.trim()) {
                        return message.warning("Please fill all fields.");
                    }
                    setResetLoading(true);
                    try {
                        await drEasyApi.post(`/api/${slug}/User/ResetPassword`, {
                            email: forgotEmail,
                            resetCode,
                            newPassword,
                        });
                        message.success("Password reset successfully!");
                        setShowForgotModal(false);
                        setActiveTab("login");
                    } catch (err) {
                        const error = err.response?.data?.errors?.[0] || err.response?.data?.message;

                        if (!error) {
                            message.error("Failed to reset password.");
                        } else if (error.includes("Invalid")) {
                            message.error("Invalid verification code.");
                        } else if (error.toLowerCase().includes("at least 6")) {
                            message.error("Passwords should be at least 6 characters.");
                        } else if (error.toLowerCase().includes("password")) {
                            message.error(error);
                        } else {
                            message.error(error);
                        }
                    } finally {
                        setResetLoading(false);
                    }
                }}
            />
        </div>
    );
}